import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { Observable, timer, map, tap } from 'rxjs';

import { LocalStorageService } from '../../../local-storage/local-storage.service';
import { UserService } from '../../../services/user.service';
import { AdminAlert } from '../interfaces/admin-alert';
import { fetchAdminAlerts, hideAdminAlert } from '../store/actions';
import { selectAdminAlertsItems } from '../store/selectors';
import { AdminAlertsState } from '../types';

@Injectable({ providedIn: 'root' })
export class AdminAlertsService {
  private store = inject<
    Store<{
      adminAlerts: AdminAlertsState;
    }>
  >(Store);
  private localStorageService = inject(LocalStorageService);
  private userService = inject(UserService);

  static readonly POOLING_INTERVAL = 10 * 60 * 1000; // 10 minutes

  constructor() {
    this.maintainLocalStorage();
  }

  getItems(): Observable<AdminAlert[]> {
    return this.store
      .select(selectAdminAlertsItems)
      .pipe(map((alerts) => alerts.filter((alert) => !this.isAlertHidden(alert.id))));
  }

  fetchAlerts(): void {
    const user = this.userService.getUser();
    if (user) {
      this.store.dispatch(fetchAdminAlerts());
    }
  }

  startPulling(): Observable<number> {
    return timer(0, AdminAlertsService.POOLING_INTERVAL).pipe(tap(() => this.fetchAlerts()));
  }

  hideAlert(id: string): void {
    const hiddenAlerts = (this.localStorageService.get('hiddenAlerts') as string[]) || [];
    if (!hiddenAlerts.includes(id)) {
      hiddenAlerts.push(id);
    }
    this.localStorageService.set('hiddenAlerts', hiddenAlerts);
    this.store.dispatch(hideAdminAlert({ payload: id }));
  }

  private isAlertHidden(id: string): boolean {
    const hiddenAlerts = (this.localStorageService.get('hiddenAlerts') as string[]) || [];
    return hiddenAlerts.includes(id);
  }

  private maintainLocalStorage(): void {
    const cleanAlertsDate = this.localStorageService.get('cleanAlertsDate');
    if (!cleanAlertsDate) {
      this.localStorageService.set('cleanAlertsDate', moment().add(7, 'days'));
    } else if (moment().isAfter(cleanAlertsDate)) {
      this.localStorageService.set('hiddenAlerts', []);
      this.localStorageService.set('cleanAlertsDate', moment().add(7, 'days'));
    }
  }
}
